/* ==========================================================================
   HYBEC AIR — "The collection" single-row carousel
   Auto-advances the track, and wires the two arrow buttons rendered either
   side of it. No cloning/infinite-loop trickery: reaching the end just
   scrolls back to the start, which reads fine for an 8-card row.
   ========================================================================== */
(function () {
  'use strict';

  var AUTOPLAY_DELAY = 3500;
  var wired = new WeakSet();

  function setup(root) {
    if (wired.has(root)) return;
    wired.add(root);

    var track = root.querySelector('[data-hy-carousel-track]');
    var prevBtn = root.querySelector('[data-hy-carousel-prev]');
    var nextBtn = root.querySelector('[data-hy-carousel-next]');
    if (!track) return;

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var timer = null;

    function step() {
      var card = track.querySelector('.hy-product-card');
      if (!card) return 0;
      var style = window.getComputedStyle(track);
      var gap = parseFloat(style.columnGap || style.gap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    }

    function atEnd() {
      return track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    }

    function advance() {
      if (atEnd()) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      }
    }

    function back() {
      if (track.scrollLeft <= 4) {
        track.scrollTo({ left: track.scrollWidth, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      }
    }

    function play() {
      if (reduceMotion) return;
      stop();
      timer = window.setInterval(advance, AUTOPLAY_DELAY);
    }

    function stop() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        advance();
        play();
      });
    }
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        back();
        play();
      });
    }

    // Pause on hover/focus/touch so autoplay never fights a manual swipe.
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', play);
    track.addEventListener('touchstart', stop, { passive: true });
    track.addEventListener('touchend', play, { passive: true });
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', play);

    /* ---------- Click-and-drag scrolling (mouse only) ----------
       overflow-x:auto already responds to trackpad/wheel/touch out of the
       box — only a mouse click-drag over the cards (the natural thing to
       try) did nothing, so that's the one gap this fills. Deliberately
       scoped to pointerType 'mouse': touch already has native swipe
       scrolling (and the browser's own gesture handling correctly tells a
       vertical page-scroll swipe apart from a horizontal carousel swipe —
       our own delta math can't), so touching this section reused to
       hijack pointer capture on every touch, blocking normal page scroll
       whenever a finger landed on the carousel. Also skips engaging at
       all when the press starts on a button or link, so setPointerCapture
       never reroutes their own click. */
    var isDragging = false;
    var dragMoved = false;
    var startX = 0;
    var startScroll = 0;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (e.target.closest('a, button')) return;
      isDragging = true;
      dragMoved = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      track.classList.add('is-dragging');
      track.setPointerCapture(e.pointerId);
      stop();
    });

    track.addEventListener('pointermove', function (e) {
      if (!isDragging) return;
      var delta = e.clientX - startX;
      if (Math.abs(delta) > 6) dragMoved = true;
      track.scrollLeft = startScroll - delta;
    });

    function endDrag(e) {
      if (!isDragging) return;
      isDragging = false;
      track.classList.remove('is-dragging');
      if (e && e.pointerId !== undefined && track.hasPointerCapture(e.pointerId)) {
        track.releasePointerCapture(e.pointerId);
      }
      play();
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // A drag that actually moved the track shouldn't also navigate via the
    // card's link underneath the pointer once released.
    track.addEventListener(
      'click',
      function (e) {
        if (dragMoved && e.target.closest('a')) {
          e.preventDefault();
          e.stopPropagation();
        }
        dragMoved = false;
      },
      true
    );

    // Pause entirely once scrolled off screen instead of ticking in the
    // background for a section the visitor has already scrolled past.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            play();
          } else {
            stop();
          }
        });
      }, { threshold: 0.2 }).observe(root);
    } else {
      play();
    }
  }

  function init() {
    document.querySelectorAll('[data-hy-carousel]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
